//#region src/consent.ts
function acceptAll(config) {
	const consent = { essential: true };
	for (const [key, value] of Object.entries(config.cookies)) {
		if (key === "essential") continue;
		consent[key] = Boolean(value);
	}
	return consent;
}
function rejectAll(config) {
	const consent = { essential: true };
	if (config) for (const key of Object.keys(config.cookies)) {
		if (key === "essential") continue;
		consent[key] = false;
	}
	return consent;
}
//#endregion
//#region src/documents/helpers.ts
const heading = (value, levelOrContext, context) => {
	const level = typeof levelOrContext === "number" ? levelOrContext : void 0;
	const ctx = typeof levelOrContext === "object" ? levelOrContext : context;
	return {
		type: "heading",
		...level !== void 0 && { level },
		value,
		...ctx && { context: ctx }
	};
};
const text = (value, context) => ({
	type: "text",
	value,
	...context && { context }
});
const bold = (value, context) => ({
	type: "bold",
	value,
	...context && { context }
});
const italic = (value, context) => ({
	type: "italic",
	value,
	...context && { context }
});
const link = (href, value, context) => ({
	type: "link",
	href,
	value,
	...context && { context }
});
const p = (children, context) => ({
	type: "paragraph",
	children: children.map((c) => typeof c === "string" ? text(c) : c),
	...context && { context }
});
const li = (children, context) => ({
	type: "listItem",
	children: children.map((c) => typeof c === "string" ? text(c) : c),
	...context && { context }
});
const ul = (items, context) => ({
	type: "list",
	items,
	...context && { context }
});
const ol = (items, context) => ({
	type: "list",
	ordered: true,
	items,
	...context && { context }
});
const section = (id, content, context) => ({
	type: "section",
	id,
	content,
	...context && { context }
});
//#endregion
//#region src/documents/cookie.ts
function buildIntroduction$1(config) {
	return section("cookie-introduction", [heading("Cookie Policy"), p([`This Cookie Policy explains how ${config.company.name} ("we", "us", or "our") uses cookies and similar tracking technologies on our services. Effective Date: ${config.effectiveDate}.`])]);
}
function buildWhatAreCookies() {
	return section("cookie-what-are-cookies", [
		heading("What Are Cookies?"),
		p(["Cookies are small text files placed on your device by websites you visit. They are widely used to make websites work more efficiently and to provide information to site owners."]),
		p(["Cookies can be \"session cookies\" (deleted when you close your browser) or \"persistent cookies\" (remain on your device until they expire or you delete them)."])
	]);
}
function buildTypes(config) {
	const types = [];
	if (config.cookies.essential) types.push({
		label: "Essential Cookies",
		description: "Required for the basic functioning of our services. These cannot be disabled."
	});
	if (config.cookies.analytics) types.push({
		label: "Analytics Cookies",
		description: "Help us understand how visitors interact with our services so we can improve them."
	});
	if (config.cookies.functional) types.push({
		label: "Functional Cookies",
		description: "Enable enhanced functionality and personalisation, such as remembering your preferences."
	});
	if (config.cookies.marketing) types.push({
		label: "Marketing Cookies",
		description: "Used to deliver advertisements more relevant to you and your interests."
	});
	if (types.length === 0) return section("cookie-types", [heading("Types of Cookies We Use"), p(["We do not currently use any cookies."])]);
	return section("cookie-types", [heading("Types of Cookies We Use"), ul(types.map((t) => li([
		bold(t.label),
		" — ",
		t.description
	])))]);
}
function buildTrackingTechnologies(config) {
	if (!config.trackingTechnologies || config.trackingTechnologies.length === 0) return null;
	return section("cookie-tracking-technologies", [
		heading("Other Tracking Technologies"),
		p(["In addition to cookies, we may use the following tracking technologies:"]),
		ul(config.trackingTechnologies.map((t) => li([t])))
	]);
}
function buildThirdParties$1(config) {
	if (!config.thirdParties || config.thirdParties.length === 0) return null;
	return section("cookie-third-parties", [
		heading("Third-Party Cookies"),
		p(["The following third parties may set cookies through our services:"]),
		ul(config.thirdParties.map((t) => li([
			bold(t.name),
			" — ",
			t.purpose,
			...t.policyUrl ? [
				" (",
				link(t.policyUrl, "Privacy Policy"),
				")"
			] : []
		])))
	]);
}
function buildConsent(config) {
	if (!config.consentMechanism) return null;
	const { hasBanner, hasPreferencePanel, canWithdraw } = config.consentMechanism;
	const items = [];
	if (hasBanner) items.push("We display a cookie consent banner when you first visit our services.");
	if (hasPreferencePanel) items.push("You can manage your cookie preferences at any time via our preference panel.");
	if (canWithdraw) items.push("You may withdraw your consent at any time; however, this will not affect the lawfulness of processing based on consent before its withdrawal.");
	if (items.length === 0) return null;
	return section("cookie-consent", [heading("Your Consent"), ul(items.map((i) => li([i])))]);
}
function buildManaging() {
	return section("cookie-managing", [
		heading("Managing Cookies"),
		p(["Most web browsers allow you to control cookies through their settings. You can:"]),
		ul([
			li(["Delete cookies already stored on your device"]),
			li(["Block cookies from being set on your device"]),
			li(["Set your browser to notify you when a cookie is being set"])
		]),
		p(["Please note that restricting cookies may impact the functionality of our services. Consult your browser's help documentation for instructions on managing cookies."])
	]);
}
function buildJurisdictionEu(config) {
	if (!config.jurisdictions.includes("eu")) return null;
	return section("cookie-jurisdiction-eu", [
		heading("European Users (GDPR)", { reason: "Required under ePrivacy Directive and GDPR" }),
		p(["If you are located in the European Economic Area, we rely on your consent as our legal basis for setting non-essential cookies. You have the right to withdraw consent at any time."]),
		p(["Essential cookies are set on the basis of our legitimate interests to provide you with a functioning service."])
	]);
}
function buildContact$1(config) {
	return section("cookie-contact", [
		heading("Contact Us"),
		p(["If you have questions about this Cookie Policy, please contact us:"]),
		ul([
			li([bold("Legal Name: "), config.company.legalName]),
			li([bold("Address: "), config.company.address]),
			li([bold("Email: "), config.company.contact])
		])
	]);
}
const SECTION_BUILDERS$1 = [
	buildIntroduction$1,
	() => buildWhatAreCookies(),
	buildTypes,
	buildTrackingTechnologies,
	buildThirdParties$1,
	buildConsent,
	() => buildManaging(),
	buildJurisdictionEu,
	buildContact$1
];
function compileCookieDocument(config) {
	return SECTION_BUILDERS$1.map((builder) => builder(config)).filter((s) => s !== null);
}
//#endregion
//#region src/documents/privacy.ts
const LEGAL_BASIS_LABELS = {
	consent: "Consent (Article 6(1)(a))",
	contract: "Performance of a contract (Article 6(1)(b))",
	legal_obligation: "Compliance with a legal obligation (Article 6(1)(c))",
	vital_interests: "Protection of vital interests (Article 6(1)(d))",
	public_task: "Performance of a public task (Article 6(1)(e))",
	legitimate_interests: "Legitimate interests (Article 6(1)(f))"
};
const RIGHTS_LABELS = {
	access: "Right to access your personal data",
	rectification: "Right to correct inaccurate data",
	erasure: "Right to request deletion of your data",
	portability: "Right to receive your data in a portable format",
	restriction: "Right to restrict how we process your data",
	objection: "Right to object to processing",
	opt_out_sale: "Right to opt out of the sale of your personal information",
	non_discrimination: "Right to non-discriminatory treatment for exercising your rights"
};
function buildIntroduction(config) {
	return section("introduction", [
		heading("Introduction"),
		p([`This Privacy Policy describes how ${config.company.name} ("we", "us", or "our") collects, uses, and shares information about you when you use our services. Effective Date: ${config.effectiveDate}.`]),
		p([`If you have questions about this policy, please contact us at ${config.company.contact}.`])
	]);
}
function buildChildrenPrivacy(config) {
	if (!config.children) return null;
	const { underAge, noticeUrl } = config.children;
	return section("children-privacy", [
		heading("Children's Privacy", { reason: "Required by COPPA" }),
		p([`Our services are not directed to children under the age of ${underAge}. We do not knowingly collect personal information from children under ${underAge}. If you believe we have collected information from a child, please contact us immediately.`]),
		...noticeUrl ? [p([
			"For more information, see our ",
			link(noticeUrl, "Children's Privacy Notice"),
			"."
		])] : []
	]);
}
function buildDataCollected(config) {
	const items = Object.entries(config.dataCollected).map(([category, fields]) => li([bold(category), ul(fields.map((f) => li([f])))]));
	return section("data-collected", [
		heading("Information We Collect"),
		p(["We collect the following categories of information:"]),
		ul(items)
	]);
}
function buildLegalBasis(config) {
	if (!config.jurisdictions.includes("eu")) return null;
	const labelled = (Array.isArray(config.legalBasis) ? config.legalBasis : [config.legalBasis]).map((b) => LEGAL_BASIS_LABELS[b] ?? b);
	return section("legal-basis", [heading("Legal Basis for Processing", { reason: "Required by GDPR Article 13" }), p([labelled.join(" and ")])]);
}
function buildDataRetention(config) {
	const items = Object.entries(config.retention).map(([category, period]) => li([
		bold(category),
		": ",
		period
	]));
	return section("data-retention", [
		heading("Data Retention"),
		p(["We retain your data for the following periods:"]),
		ul(items)
	]);
}
function buildCookies(config) {
	const enabled = [];
	if (config.cookies.essential) enabled.push("Essential cookies — required for the service to function");
	if (config.cookies.analytics) enabled.push("Analytics cookies — help us understand how the service is used");
	if (config.cookies.marketing) enabled.push("Marketing cookies — used to deliver relevant advertisements");
	if (enabled.length === 0) return section("cookies", [heading("Cookies and Tracking"), p(["We do not use cookies or similar tracking technologies."])]);
	return section("cookies", [
		heading("Cookies and Tracking"),
		p(["We use the following types of cookies and tracking technologies:"]),
		ul(enabled.map((e) => li([e])))
	]);
}
function buildThirdParties(config) {
	if (config.thirdParties.length === 0) return section("third-parties", [heading("Third-Party Services"), p(["We do not share your personal information with third parties except as required by law."])]);
	return section("third-parties", [
		heading("Third-Party Services"),
		p(["We share data with the following third-party services:"]),
		ul(config.thirdParties.map((t) => li([
			t.policyUrl ? link(t.policyUrl, t.name) : bold(t.name),
			" — ",
			t.purpose
		])))
	]);
}
function buildUserRights(config) {
	if (config.userRights.length === 0) return null;
	const items = config.userRights.map((right) => {
		return li([RIGHTS_LABELS[right] ?? right]);
	});
	return section("user-rights", [
		heading("Your Rights"),
		p(["You have the following rights regarding your personal data:"]),
		ul(items)
	]);
}
function buildGdprSupplement(config) {
	if (!config.jurisdictions.includes("eu")) return null;
	return section("gdpr-supplement", [
		heading("GDPR Supplemental Disclosures", { reason: "Required by GDPR Article 13" }),
		p(["This section applies to individuals in the European Economic Area (EEA) under the General Data Protection Regulation (GDPR)."]),
		p([
			"Data Controller: ",
			bold(config.company.legalName),
			`, ${config.company.address}`
		]),
		p(["In addition to the rights listed above, you have the right to lodge a complaint with your local data protection authority if you believe we have not handled your data in accordance with applicable law."]),
		p(["If we transfer your personal data outside the EEA, we ensure adequate safeguards are in place in accordance with GDPR requirements."])
	]);
}
function buildCcpaSupplement(config) {
	if (!config.jurisdictions.includes("ca")) return null;
	return section("ccpa-supplement", [
		heading("California Privacy Rights (CCPA)", { reason: "Required by CCPA" }),
		p(["If you are a California resident, you have the following additional rights:"]),
		ul([
			li(["Right to Know — You may request disclosure of the personal information we collect, use, and share about you."]),
			li(["Right to Delete — You may request deletion of personal information we have collected about you."]),
			li(["Right to Opt-Out — You may opt out of the sale of your personal information."]),
			li(["Right to Non-Discrimination — We will not discriminate against you for exercising your CCPA rights."])
		])
	]);
}
function buildContact(config) {
	return section("contact", [
		heading("Contact Us"),
		p(["Contact us:"]),
		ul([
			li([
				bold("Legal Name:"),
				" ",
				config.company.legalName
			]),
			li([
				bold("Address:"),
				" ",
				config.company.address
			]),
			li([
				bold("Email:"),
				" ",
				config.company.contact
			])
		])
	]);
}
const SECTION_BUILDERS = [
	buildIntroduction,
	buildChildrenPrivacy,
	buildDataCollected,
	buildLegalBasis,
	buildDataRetention,
	buildCookies,
	buildThirdParties,
	buildUserRights,
	buildGdprSupplement,
	buildCcpaSupplement,
	buildContact
];
function compilePrivacyDocument(config) {
	return SECTION_BUILDERS.map((builder) => builder(config)).filter((s) => s !== null);
}
//#endregion
//#region src/documents/index.ts
function compile(input) {
	if (input.type === "privacy") {
		const { type: _, ...config } = input;
		return {
			type: "document",
			policyType: "privacy",
			sections: compilePrivacyDocument(config)
		};
	}
	const { type: _, ...config } = input;
	return {
		type: "document",
		policyType: "cookie",
		sections: compileCookieDocument(config)
	};
}
//#endregion
//#region src/types.ts
function isOpenPolicyConfig(value) {
	if (value === null || typeof value !== "object") return false;
	const obj = value;
	return "company" in obj && "effectiveDate" in obj && !("type" in obj);
}
//#endregion
//#region src/user-rights.ts
const RIGHTS_BY_JURISDICTION = {
	eu: [
		"access",
		"rectification",
		"erasure",
		"portability",
		"restriction",
		"objection"
	],
	ca: [
		"access",
		"erasure",
		"opt_out_sale",
		"non_discrimination"
	]
};
const CANONICAL_ORDER = [
	"access",
	"rectification",
	"erasure",
	"portability",
	"restriction",
	"objection",
	"opt_out_sale",
	"non_discrimination"
];
function deriveUserRights(jurisdictions) {
	const set = /* @__PURE__ */ new Set();
	for (const j of jurisdictions) for (const right of RIGHTS_BY_JURISDICTION[j] ?? []) set.add(right);
	return CANONICAL_ORDER.filter((right) => set.has(right));
}
//#endregion
//#region src/validate.ts
function validatePrivacyPolicy(config) {
	const issues = [];
	if (!config.effectiveDate) issues.push({
		level: "error",
		message: "effectiveDate is required"
	});
	if (!config.company.name) issues.push({
		level: "error",
		message: "company.name is required"
	});
	if (!config.company.legalName) issues.push({
		level: "error",
		message: "company.legalName is required"
	});
	if (!config.company.address) issues.push({
		level: "error",
		message: "company.address is required"
	});
	if (!config.company.contact) issues.push({
		level: "error",
		message: "company.contact is required"
	});
	if (Object.keys(config.dataCollected).length === 0) issues.push({
		level: "error",
		message: "dataCollected must have at least one entry"
	});
	if (config.jurisdictions.includes("eu")) {
		const basisArray = Array.isArray(config.legalBasis) ? config.legalBasis : [config.legalBasis];
		if (basisArray.length === 0 || basisArray.length === 1 && !basisArray[0]) issues.push({
			level: "error",
			message: "GDPR requires a legalBasis"
		});
	}
	if (config.children) {
		if (config.children.underAge <= 0) issues.push({
			level: "error",
			message: "children.underAge must be a positive number"
		});
	}
	return issues;
}
//#endregion
//#region src/validate-config.ts
const PRIVACY_REQUIRED = "dataCollected";
function validateOpenPolicyConfig(config) {
	const issues = [];
	if (!config.effectiveDate) issues.push({
		level: "error",
		message: "effectiveDate is required"
	});
	if (!config.company?.name) issues.push({
		level: "error",
		message: "company.name is required"
	});
	if (!config.company?.legalName) issues.push({
		level: "error",
		message: "company.legalName is required"
	});
	if (!config.company?.address) issues.push({
		level: "error",
		message: "company.address is required"
	});
	if (!config.company?.contact) issues.push({
		level: "error",
		message: "company.contact is required"
	});
	if (!config.jurisdictions || config.jurisdictions.length === 0) issues.push({
		level: "error",
		message: "jurisdictions must have at least one entry"
	});
	const wantPrivacy = shouldEmit("privacy", config);
	const wantCookie = shouldEmit("cookie", config);
	if (!wantPrivacy && !wantCookie) issues.push({
		level: "error",
		message: "Config must produce at least one policy — provide data-handling fields (dataCollected, legalBasis, retention, children) or cookies"
	});
	if (config.policies) for (const category of config.policies) {
		if (category === "privacy" && !hasAnyPrivacyField$1(config)) issues.push({
			level: "error",
			message: "policies includes \"privacy\" but no data-handling fields are set — add dataCollected, legalBasis, retention, or children"
		});
		if (category === "cookie" && !config.cookies) issues.push({
			level: "error",
			message: "policies includes \"cookie\" but cookies is not set"
		});
	}
	if (wantPrivacy && !config.dataCollected) issues.push({
		level: "warning",
		message: `${PRIVACY_REQUIRED} is not set — the privacy policy will render a placeholder section`
	});
	return issues;
}
function hasAnyPrivacyField$1(config) {
	return config.dataCollected !== void 0 || config.legalBasis !== void 0 || config.retention !== void 0 || config.children !== void 0;
}
//#endregion
//#region src/validate-cookie.ts
function validateCookiePolicy(config) {
	const issues = [];
	if (!config.effectiveDate) issues.push({
		level: "error",
		message: "effectiveDate is required"
	});
	if (!config.company.name) issues.push({
		level: "error",
		message: "company.name is required"
	});
	if (!config.company.legalName) issues.push({
		level: "error",
		message: "company.legalName is required"
	});
	if (!config.company.address) issues.push({
		level: "error",
		message: "company.address is required"
	});
	if (!config.company.contact) issues.push({
		level: "error",
		message: "company.contact is required"
	});
	const { essential, analytics, functional, marketing } = config.cookies;
	if (!essential && !analytics && !functional && !marketing) issues.push({
		level: "error",
		message: "At least one cookie type must be enabled (essential, analytics, functional, or marketing)"
	});
	if (!config.consentMechanism) issues.push({
		level: "warning",
		message: "consentMechanism is not provided — consider describing how users can manage cookie consent"
	});
	if (config.jurisdictions.includes("eu") && config.consentMechanism && !config.consentMechanism.canWithdraw) issues.push({
		level: "warning",
		message: "GDPR requires that users can withdraw cookie consent — consider setting consentMechanism.canWithdraw to true"
	});
	return issues;
}
//#endregion
//#region src/index.ts
const PRIVACY_FIELDS = [
	"dataCollected",
	"legalBasis",
	"retention",
	"children"
];
function hasAnyPrivacyField(config) {
	return PRIVACY_FIELDS.some((field) => config[field] !== void 0);
}
function hasCookieField(config) {
	return config.cookies !== void 0;
}
function shouldEmit(category, config) {
	if (config.policies) return config.policies.includes(category);
	return category === "privacy" ? hasAnyPrivacyField(config) : hasCookieField(config);
}
const EMPTY_COOKIES = { essential: true };
function expandOpenPolicyConfig(config) {
	const inputs = [];
	if (shouldEmit("privacy", config)) inputs.push({
		type: "privacy",
		company: config.company,
		effectiveDate: config.effectiveDate,
		jurisdictions: config.jurisdictions,
		dataCollected: config.dataCollected ?? {},
		legalBasis: config.legalBasis ?? [],
		retention: config.retention ?? {},
		cookies: config.cookies ?? EMPTY_COOKIES,
		thirdParties: config.thirdParties ?? [],
		userRights: deriveUserRights(config.jurisdictions),
		children: config.children
	});
	if (shouldEmit("cookie", config)) inputs.push({
		type: "cookie",
		company: config.company,
		effectiveDate: config.effectiveDate,
		jurisdictions: config.jurisdictions,
		cookies: config.cookies ?? EMPTY_COOKIES,
		thirdParties: config.thirdParties ?? [],
		trackingTechnologies: config.trackingTechnologies,
		consentMechanism: config.consentMechanism
	});
	return inputs;
}
//#endregion
export { acceptAll, bold, compile, deriveUserRights, expandOpenPolicyConfig, heading, isOpenPolicyConfig, italic, li, link, ol, p, rejectAll, section, shouldEmit, text, ul, validateCookiePolicy, validateOpenPolicyConfig, validatePrivacyPolicy };

//# sourceMappingURL=index.js.map